import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import GuideDetailSheet from "@/components/GuideDetailSheet";
import { Loader2, Plus, BookOpenCheck, Sparkles } from "lucide-react";

const GUIDE_TYPE_LABEL = { attic: "Ático", basement: "Sótano", commercial: "Comercial", soundproofing: "Insonorización" };
const AUDIENCE_LABEL = { homeowners: "Propietarios", architects: "Arquitectos", commercial: "Comercial", investors: "Inversionistas" };
const STATUS = {
  draft: { label: "Borrador", cls: "bg-[#71717A]/10 text-[#71717A]" },
  pending_review: { label: "En revisión", cls: "bg-[#F59E0B]/10 text-[#F59E0B]" },
  approved: { label: "Aprobada", cls: "bg-[#0055FF]/10 text-[#0055FF]" },
  published: { label: "Publicada", cls: "bg-[#10B981]/10 text-[#10B981]" },
};
const EMPTY_FORM = { guide_type: "attic", audience: "homeowners", state: "", zip_code: "" };

export default function GuidesPage() {
  const { activeTenantId, activeTenant } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [selected, setSelected] = useState(null);
  const [sheetOpen, setSheetOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/guides");
      setItems(res.data);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (activeTenantId) load(); }, [activeTenantId, load]);

  const openCreate = () => { setForm(EMPTY_FORM); setOpen(true); };

  const generate = async () => {
    setGenerating(true);
    try {
      const res = await api.post("/guides/generate", form);
      toast.success(res.data.error_message ? "Se guardó el borrador, pero la generación falló" : "Guía generada");
      setOpen(false);
      load();
      setSelected(res.data);
      setSheetOpen(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo generar la guía");
    } finally {
      setGenerating(false);
    }
  };

  const openDetail = (item) => { setSelected(item); setSheetOpen(true); };
  const onChanged = (updated) => {
    setSelected(updated);
    setItems((prev) => prev.map((i) => (i.id === updated.id ? updated : i)));
  };

  return (
    <div className="space-y-6" data-testid="guides-page">
      <div className="flex items-center justify-between">
        <p className="text-sm text-[#71717A] max-w-xl">
          Guías educativas públicas generadas con IA desde la base de conocimiento: el cliente que llama
          preguntando "¿cómo se hace mi ático?" recibe un link con la respuesta.
        </p>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-guide-button" onClick={openCreate} className="bg-[#0055FF] hover:bg-[#0044CC] rounded-md">
              <Plus className="h-4 w-4 mr-1" /> Generar guía
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg" data-testid="guide-generate-dialog">
            <DialogHeader><DialogTitle className="font-heading">Generar guía</DialogTitle></DialogHeader>
            <div className="space-y-4 py-2">
              <div className="space-y-1.5">
                <Label className="text-xs text-[#71717A]">Tema</Label>
                <Select value={form.guide_type} onValueChange={(v) => setForm({ ...form, guide_type: v })}>
                  <SelectTrigger data-testid="guide-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(GUIDE_TYPE_LABEL).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-[#71717A]">Audiencia</Label>
                <Select value={form.audience} onValueChange={(v) => setForm({ ...form, audience: v })}>
                  <SelectTrigger data-testid="guide-audience-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(AUDIENCE_LABEL).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs text-[#71717A]">Estado</Label>
                  <Input data-testid="guide-state-input" value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} placeholder="NY" maxLength={2} />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-[#71717A]">Código postal</Label>
                  <Input data-testid="guide-zip-input" value={form.zip_code} onChange={(e) => setForm({ ...form, zip_code: e.target.value })} placeholder="11201" />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button
                data-testid="generate-guide-button" onClick={generate}
                disabled={generating || activeTenant?.status === "suspended"}
                title={activeTenant?.status === "suspended" ? "Cuenta suspendida" : undefined}
                className="bg-[#0055FF] hover:bg-[#0044CC] rounded-md"
              >
                {generating ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Sparkles className="h-4 w-4 mr-1" />} Generar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="rounded-sm border-[#E4E4E7] shadow-none">
        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-[#0055FF]" /></div>
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center py-16 text-center">
            <BookOpenCheck className="h-8 w-8 text-[#A1A1AA]" />
            <p className="mt-3 text-sm text-[#71717A]">Todavía no generaste guías.</p>
          </div>
        ) : (
          <Table data-testid="guides-table">
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead>Título</TableHead>
                <TableHead>Tipo</TableHead>
                <TableHead>Jurisdicción</TableHead>
                <TableHead>Estado</TableHead>
                <TableHead>Revisión</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((g) => {
                const s = STATUS[g.status] || STATUS.draft;
                return (
                  <TableRow key={g.id} className="cursor-pointer" onClick={() => openDetail(g)} data-testid={`guide-row-${g.id}`}>
                    <TableCell className="font-medium max-w-sm truncate">{g.title || g.topic}</TableCell>
                    <TableCell className="text-[#71717A]">{GUIDE_TYPE_LABEL[g.guide_type]}</TableCell>
                    <TableCell className="text-[#71717A]">{g.state ? `${g.state}${g.zip_code ? " " + g.zip_code : ""}` : "-"}</TableCell>
                    <TableCell><Badge className={`border-0 text-[10px] uppercase tracking-wider rounded-sm ${s.cls}`}>{s.label}</Badge></TableCell>
                    <TableCell>
                      {g.requires_review && <Badge className="bg-[#EF4444]/10 text-[#EF4444] border-0 text-[10px] rounded-sm">Requiere revisión</Badge>}
                      {g.error_message && <Badge className="bg-[#EF4444]/10 text-[#EF4444] border-0 text-[10px] rounded-sm">Error de generación</Badge>}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </Card>

      <GuideDetailSheet guide={selected} open={sheetOpen} onOpenChange={setSheetOpen} onChanged={onChanged} />
    </div>
  );
}
